import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import ContactsTable from '../components/contacts/ContactsTable';
import ContactForm from '../components/contacts/ContactForm';
import LinkDealDialog from '../components/contacts/LinkDealDialog';
import QuickEditDialog from '../components/contacts/QuickEditDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialFormData = {
  company_name: '',
  shortcode: '',
  dba: '',
  industry: '',
  name: '',
  phones: [{ phone: '', type: 'Main' }],
  emails: [{ email: '', type: 'Business' }],
  website: [],
  source_company: '',
  source_contact: '',
  campaign: '',
  additional_info: '',
  business_description: '',
  third_party_id: '',
  addresses: [{ type: 'Main', street1: '', street2: '', city: '', state: '', zip: '', country: 'United States' }],
  is_funder: false,
  is_broker: false,
  is_merchant: false,
  is_iso: false,
  is_collector: false,
  is_credit_card_processor: false,
  is_vendor: false,
  is_investor: false,
  is_partner: false,
  is_general: false,
  is_other: false,
  access_permission: 'Everyone',
  broker_commission_rate: '',
  owner_name: '',
  owner_ssn: '',
  owner_dob: '',
  owner_address: '',
  owner_city: '',
  owner_state: '',
  owner_zip: '',
  phone: '',
  email: '',
  business_address: '',
  business_street2: '',
  business_city: '',
  business_state: '',
  business_zip: '',
  ein: '',
  state_of_incorporation: '',
  business_start_date: '',
  is_person: false,
  dob: '',
  home_address: { street1: '', street2: '', city: '', state: '', zip: '' },
  collects_fees: false
};

const Contacts = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState(initialFormData);
  const [sensitiveFormData, setSensitiveFormData] = useState({ bank_account_full: '', routing_number_full: '', bank_name: '' });
  
  // Link Deal Dialog state
  const [linkDealOpen, setLinkDealOpen] = useState(false);
  const [linkDealContact, setLinkDealContact] = useState(null);
  
  // Quick Edit Dialog state
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [quickEditContact, setQuickEditContact] = useState(null);

  const isAdmin = user?.roles?.includes('admin');

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`, { headers: getAuthHeaders() });
      setContacts(response.data);
      setFilteredContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Filter contacts based on search and type
  useEffect(() => {
    let filtered = [...contacts];
    
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.company_name?.toLowerCase().includes(lowerQuery) ||
        c.shortcode?.toLowerCase().includes(lowerQuery) ||
        c.dba?.toLowerCase().includes(lowerQuery) ||
        c.name?.toLowerCase().includes(lowerQuery) ||
        c.phone?.toLowerCase().includes(lowerQuery) ||
        c.email?.toLowerCase().includes(lowerQuery)
      );
    }
    
    if (filterType && filterType !== 'all') {
      const typeMap = {
        'funder': 'is_funder',
        'broker': 'is_broker',
        'merchant': 'is_merchant',
        'iso': 'is_iso',
        'investor': 'is_investor',
        'referrer': 'is_referrer',
        'person': 'is_person',
      };
      const field = typeMap[filterType];
      if (field) {
        filtered = filtered.filter(c => c[field]);
      }
    }
    
    setFilteredContacts(filtered);
  }, [contacts, searchQuery, filterType]);

  // Open full contact form dialog (for creating new contacts)
  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      
      let phonesData = [{ phone: '', type: 'Main' }];
      if (contact.phones?.length > 0) {
        phonesData = contact.phones;
      } else if (contact.phone) {
        phonesData = [{ phone: contact.phone, type: 'Main' }];
      }
      
      let emailsData = [{ email: '', type: 'Business' }];
      if (contact.emails?.length > 0) {
        emailsData = contact.emails;
      } else if (contact.email) {
        emailsData = [{ email: contact.email, type: 'Business' }];
      }
      
      setFormData({
        ...initialFormData,
        ...contact,
        is_funder: contact.is_funder === true,
        is_broker: contact.is_broker === true,
        is_merchant: contact.is_merchant === true,
        is_iso: contact.is_iso === true,
        is_collector: contact.is_collector === true,
        is_credit_card_processor: contact.is_credit_card_processor === true,
        is_vendor: contact.is_vendor === true,
        is_investor: contact.is_investor === true,
        is_partner: contact.is_partner === true,
        is_general: contact.is_general === true,
        is_other: contact.is_other === true,
        collects_fees: contact.collects_fees === true,
        phones: phonesData,
        emails: emailsData,
        addresses: contact.addresses?.length > 0 ? contact.addresses : initialFormData.addresses,
        website: contact.website || [],
        owner_name: contact.owner_name || '',
        owner_ssn: contact.owner_ssn || '',
        owner_dob: contact.owner_dob?.split('T')[0] || '',
        owner_address: contact.owner_address || '',
        phone: contact.phone || '',
        email: contact.email || '',
        business_address: contact.business_address || '',
        business_street2: contact.business_street2 || '',
        ein: contact.ein || '',
        state_of_incorporation: contact.state_of_incorporation || '',
        business_start_date: contact.business_start_date?.split('T')[0] || '',
        is_person: contact.is_person === true,
        dob: contact.dob?.split('T')[0] || '',
        home_address: { ...initialFormData.home_address, ...(contact.home_address || {}) },
        business_description: contact.business_description || '',
        broker_commission_rate: contact.broker_commission_rate || ''
      });
    } else {
      setEditingContact(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const toNumberOrNull = (val) => {
      if (val === '' || val === null || val === undefined) return null;
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    };
    
    const cleanedData = {
      ...formData,
      phone: formData.phone || '',
      email: formData.email || '',
      phones: (formData.phones || []).filter(p => p.phone?.trim()),
      emails: (formData.emails || []).filter(e => e.email?.trim()),
      addresses: formData.addresses.filter(a => a.street1 || a.city),
      dob: formData.dob || null,
      business_start_date: formData.business_start_date || null,
      broker_commission_rate: toNumberOrNull(formData.broker_commission_rate),
      referrer_contact_id: formData.is_iso ? (formData.referrer_contact_id || null) : null,   // 10/19: ISO's referrer
      referral_split_pct: formData.is_referrer ? toNumberOrNull(formData.referral_split_pct) : null   // 10/20: referrer's share of the 2% pool
    };
    // Person records: the full name IS the record name.
    if (cleanedData.is_person && !cleanedData.name) cleanedData.name = cleanedData.company_name;

    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, cleanedData, { headers: getAuthHeaders() });
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API}/contacts`, cleanedData, { headers: getAuthHeaders() });
        toast.success('Contact created successfully');
      }
      setDialogOpen(false);
      fetchContacts();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Operation failed';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
      console.error('Contact save error:', errorDetail);
    }
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedContacts(checked ? filteredContacts.map(c => c.id) : []);
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedContacts.length} selected contact(s)? This cannot be undone.`)) return;
    
    try {
      for (const id of selectedContacts) {
        await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeaders() });
      }
      toast.success(`Deleted ${selectedContacts.length} contacts`);
      setSelectedContacts([]);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete some contacts');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Shortcode', 'Company Name', 'Contact Name', 'DBA', 'Phone', 'Email', 'Type'];
    const rows = filteredContacts.map(c => {
      const types = [];
      if (c.is_funder) types.push('Funder');
      if (c.is_broker) types.push('Broker');
      if (c.is_iso) types.push('ISO');
      if (c.is_merchant) types.push('Merchant');
      if (c.is_investor) types.push('Investor');
      if (c.is_referrer) types.push('Referrer');
      if (c.is_partner) types.push('Partner');
      
      return [
        c.shortcode || '',
        c.company_name || '',
        c.name || '',
        c.dba || '',
        c.phone || c.phones?.[0]?.phone || '',
        c.email || c.emails?.[0]?.email || '',
        types.join(', ')
      ];
    });
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Navigate to Contact Profile page
  const handleViewProfile = (contact) => {
    navigate(`/contacts/${contact.id}`);
  };

  // Open Quick Edit dialog
  const handleQuickEdit = (contact) => {
    setQuickEditContact(contact);
    setQuickEditOpen(true);
  };

  // Create Deal - navigate to Deals with prefilled merchant data
  const handleCreateDeal = (contact) => {
    sessionStorage.setItem('preFillDeal', JSON.stringify({
      company_id: contact.id,
      company_name: contact.company_name,
      shortcode: contact.shortcode,
      dba: contact.dba,
      industry: contact.industry,
      name: contact.name,
      phone: contact.phone || '',
      email: contact.email || '',
      business_address: contact.business_address || '',
      owner_name: contact.owner_name || contact.name || '',
      owner_phone: contact.phone || '',
      owner_email: contact.email || '',
      ein: contact.ein || '',
      monthly_revenue: contact.monthly_revenue || ''
    }));
    navigate('/deals?createNew=true');
  };

  // Link Deal dialog
  const handleLinkDeal = (contact) => {
    setLinkDealContact(contact);
    setLinkDealOpen(true);
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0" data-testid="contacts-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Address Book</h1>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search the Address Book..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
                data-testid="contacts-search"
              />
            </div>
            
            {/* Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="contacts-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="funder">Funders</SelectItem>
                <SelectItem value="broker">Brokers</SelectItem>
                <SelectItem value="iso">ISOs</SelectItem>
                <SelectItem value="merchant">Merchants</SelectItem>
                <SelectItem value="investor">Investors</SelectItem>
                <SelectItem value="referrer">Referrers</SelectItem>
                <SelectItem value="person">People</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Export */}
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-contacts">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            
            {/* Create */}
            {isAdmin && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => handleOpenDialog()} data-testid="add-contact-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Record
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
                  <DialogHeader>
                    <DialogTitle>
                      {editingContact ? 'Edit Address Book Record' : 'New Address Book Record'}
                    </DialogTitle>
                  </DialogHeader>
                  <ContactForm
                    formData={formData}
                    setFormData={setFormData}
                    onSubmit={handleSubmit}
                    onCancel={() => setDialogOpen(false)}
                    editingContact={editingContact}
                    sensitiveData={sensitiveFormData}
                    onSensitiveDataChange={setSensitiveFormData}
                    getAuthHeaders={getAuthHeaders}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Selected contacts actions - bulk delete */}
        {selectedContacts.length > 0 && isAdmin && (
          <Card className="mb-4 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-blue-900 font-medium">
                  {selectedContacts.length} contact(s) selected
                </span>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleDeleteSelected}
                  data-testid="delete-selected-btn"
                >
                  Delete Selected
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                The Address Book is empty. Add your first record.
              </div>
            ) : (
              <ContactsTable
                contacts={filteredContacts}
                selectedContacts={selectedContacts}
                onSelectContact={handleSelectContact}
                onSelectAll={handleSelectAll}
                onQuickEdit={handleQuickEdit}
                onCreateDeal={handleCreateDeal}
                onLinkDeal={handleLinkDeal}
                onViewProfile={handleViewProfile}
              />
            )}
          </CardContent>
        </Card>
        
        {/* Link Deal Dialog */}
        <LinkDealDialog
          open={linkDealOpen}
          onOpenChange={setLinkDealOpen}
          contact={linkDealContact}
          getAuthHeaders={getAuthHeaders}
          onSuccess={() => {
            toast.success('Deal linked successfully');
          }}
        />
        
        {/* Quick Edit Dialog */}
        <QuickEditDialog
          open={quickEditOpen}
          onOpenChange={setQuickEditOpen}
          contact={quickEditContact}
          getAuthHeaders={getAuthHeaders}
          onSuccess={fetchContacts}
        />
      </div>
    </Layout>
  );
};

export default Contacts;
